// The secret that lets a guest come back as themselves.
//
// A guest's chat pass lasts about a day. After that the only thing the browser
// still carried was the visitor id, which any script on the shop can read, so
// the server will not hand a conversation back on the strength of it: the
// visitor returned as a stranger. nasnav-backend MR 2552 (MEE-603) adds a key
// that the browser keeps instead:
//
//   - sent as `guestKey` on the guest-session call, when the browser has one;
//   - answered with a NEW `guestKey` and `guestKeyExpiresAt` on every
//     response. The key that was sent is dead the moment it is accepted;
//   - a key presented twice is treated as stolen: the server revokes it and
//     every key after it, and the visitor starts again as a stranger.
//
// So the rules here are: keep only the newest key, take it out of storage
// before sending it, and never let two requests carry the same one. Every
// failure on the server's side looks the same (a fresh guest), so nothing here
// branches on why a key was refused.

const STORAGE_PREFIX = "meetusvr.guestKey.";
const DB_NAME = "meetusvr";
const DB_STORE = "guestKey";
const DB_VERSION = 1;
// Holding the key is a one-at-a-time job across every tab of this shop: two
// tabs opening together must not both send the key they found.
const LOCK_PREFIX = "meetusvr-guest-key-";

const storageName = (orgId) => `${STORAGE_PREFIX}${orgId}`;

const local = () => {
  try {
    return window.localStorage;
  } catch (e) {
    return null;
  }
};

// The key is written twice. Clearing a site's cookies and local storage, which
// is what most people mean by clearing their browser, leaves its database
// alone - that is why Eleven's visitor survived it. The database is the copy
// that lasts; local storage is the one that can be read and emptied at once.
const openDb = () =>
  new Promise((resolve) => {
    let request;
    try {
      if (typeof indexedDB === "undefined" || !indexedDB) return resolve(null);
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (e) {
      return resolve(null);
    }
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(DB_STORE)) {
        request.result.createObjectStore(DB_STORE);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
    request.onblocked = () => resolve(null);
  });

const inDb = (mode, act) =>
  openDb().then(
    (db) =>
      new Promise((resolve) => {
        if (!db) return resolve(null);
        try {
          const tx = db.transaction(DB_STORE, mode);
          const result = act(tx.objectStore(DB_STORE));
          tx.oncomplete = () => {
            db.close();
            resolve(result && "result" in result ? result.result : null);
          };
          tx.onerror = () => {
            db.close();
            resolve(null);
          };
        } catch (e) {
          db.close();
          resolve(null);
        }
      })
  );

const isUsable = (saved) => {
  if (!saved || typeof saved.key !== "string" || !saved.key) return false;
  if (!saved.expiresAt) return true;
  const expires = Date.parse(saved.expiresAt);
  return Number.isNaN(expires) || expires > Date.now();
};

const parse = (raw) => {
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
};

/**
 * Take this shop's key out of storage, for sending.
 *
 * Taken, not read: once it leaves for the server it is spent whatever the
 * answer, and a copy left behind is a copy that could be sent a second time.
 * If the answer never arrives the visitor starts as a new guest next time,
 * which is a smaller loss than a key sent twice revoking everything.
 *
 * @param {string|number} orgId the shop
 * @returns {Promise<string|null>} the key, or null when there is none worth sending
 */
export const takeGuestKey = async (orgId) => {
  const name = storageName(orgId);
  let saved = null;
  const store = local();
  try {
    saved = parse(store && store.getItem(name));
    if (store) store.removeItem(name);
  } catch (e) {
    saved = null;
  }
  const fromDb = await inDb("readwrite", (os) => {
    const read = os.get(name);
    os.delete(name);
    return read;
  });
  if (!isUsable(saved)) saved = parse(fromDb);
  return isUsable(saved) ? saved.key : null;
};

/**
 * Keep the key the server just handed out, replacing any other.
 *
 * @param {string|number} orgId the shop
 * @param {string} key the new `guestKey`
 * @param {string} [expiresAt] the new `guestKeyExpiresAt`
 */
export const keepGuestKey = async (orgId, key, expiresAt) => {
  if (typeof key !== "string" || !key) return;
  const name = storageName(orgId);
  const saved = { key, expiresAt: expiresAt || null };
  const store = local();
  try {
    if (store) store.setItem(name, JSON.stringify(saved));
  } catch (e) {
    // Storage full or blocked: the database copy may still hold.
  }
  await inDb("readwrite", (os) => os.put(saved, name));
};

/**
 * Run one exchange of this shop's key at a time, across every tab.
 *
 * Within one page the guest-session request is already shared by everyone who
 * asks. Two tabs are two pages: both would find the same key and both would
 * send it, which the server reads as theft. Where the browser offers locks,
 * the second tab waits for the first and then finds the key it left.
 *
 * @param {string|number} orgId the shop
 * @param {() => Promise<T>} work the exchange
 * @returns {Promise<T>}
 * @template T
 */
export const withGuestKeyLock = (orgId, work) => {
  const locks = typeof navigator !== "undefined" && navigator.locks;
  if (!locks || typeof locks.request !== "function") return work();
  return locks.request(`${LOCK_PREFIX}${orgId}`, work);
};
